import { AttributeValue, QueryProps, TelemetryData, Total } from '@ui/interfaces'

interface Waffle {
  x: number
  y: number
  index: number
}

const GROUP_RATIO_THRESHOLD = 1

export const makeWaffles = (chartData: AttributeValue[], wafflesHigh: number, wafflesWide: number) => {
  if (chartData.length === 0) return []
  const array: Waffle[][] = []
  const max = chartData.length
  const waffle: Waffle[] = []
  let index = 0,
    curr = 1,
    accu = chartData[0].ratio

  for (let x = 0; x < wafflesWide; x++)
    for (let y = wafflesHigh - 1; y >= 0; y--) {
      if (curr > accu && index < max) {
        const r = chartData[++index]?.ratio
        accu += r
      }
      if (index != max) {
        waffle.push({ x, y, index })
        curr++
      } else {
        // I don't understand how we get here but it produces missing buckets, probably rounding
      }
    }
  array.push(waffle)

  return array
}

const makeQueryProps = (attributeValue: string): QueryProps => {
  let operator = ''
  let value = ''

  switch (attributeValue) {
    case '':
      operator = '='
      value = ''
      break
    case null:
      operator = 'is'
      value = 'null'
      break
    // TODO: figure out nulls
    case 'null':
      operator = 'is'
      value = 'null'
      break
    default:
      if (typeof attributeValue === 'object') {
        operator = '='
        value = `'${attributeValue}'`
      } else {
        operator = '='
        value = `'${attributeValue}'`
      }
  }
  return {
    operator,
    value,
  }
}

export const getChartDataBy = (raw: TelemetryData, key: string, chartBy: string) => {
  interface Accumulated {
    [count: string]: number
    size: number
  }

  const total: Total = {
    count: 0,
    size: 0,
  }

  // find the total count and size
  const data = raw?.telemetry?.topValues || []

  data.forEach((attr: AttributeValue) => {
    if (attr.value === 'datableTotal') {
      total.count = attr.count
      total.size = Number(attr.size)
    }
  })

  // map through rest of data
  const chartData: AttributeValue[] = []
  const accumulated: Accumulated = {
    count: 0,
    size: 0,
  }

  data.forEach((attr: AttributeValue) => {
    const ratio = (Number(attr[chartBy as keyof AttributeValue]) / total[chartBy]) * 100
    if (attr.value !== 'datableTotal' && ratio > GROUP_RATIO_THRESHOLD) {
      const value = attr.value === 'datableNull' ? 'null' : attr.value
      chartData.push({
        key: key,
        value: value,
        count: attr.count,
        size: Number(attr.size),
        ratio: ratio,
        queryProps: makeQueryProps(value),
      })
      accumulated.count += Number(attr.count)
      accumulated.size += Number(attr.size)
    }
  })

  // make an Other category by subtracting what we have from the Total
  if (accumulated[chartBy] < total[chartBy]) {
    const ratio = ((total[chartBy] - accumulated[chartBy]) / total[chartBy]) * 100
    chartData.push({
      key: key,
      value: 'Other',
      count: total.count - accumulated.count,
      size: total.size - accumulated.size,
      ratio: ratio,
      queryProps: { operator: 'is', value: 'not null' },
    })
  }

  // Fix rounding issues
  const totalRatios = chartData.reduce((acc, item) => acc + item.ratio, 0)
  const error = 100 - totalRatios

  // error will equal 100 if there's no chart data...
  if (error !== 0 && error !== 100) {
    // Find the index of the largest/smallest ratio to adjust.
    // if the error is positive, add it to the max. If it's negative, add it (a minus number) to the min.
    const indexToAdjust =
      error > 0
        ? chartData.findIndex((item) => item.ratio === Math.max(...chartData.map((r) => r.ratio)))
        : chartData.findIndex((item) => item.ratio === Math.min(...chartData.map((r) => r.ratio)))

    // Adjust the ratio to fix rounding error.
    chartData[indexToAdjust].ratio += error
  }

  return {
    data: chartData,
    total: total,
  }
}
